import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component } from '@angular/core';
import { ContentBlock, ContentBlockType } from '@tytapp/content';
import { ContentBlockEditorComponent } from '@tytapp/content/content-block-editor-component';
import { ContentBlockViewComponent } from '@tytapp/content/content-block-view-component';

export interface FeatureListItem {
    title: string;
    summary?: string;
    image?: string;
    icon?: string;
    url?: string;
}

export interface FeatureListBlock extends ContentBlock {
    title?: string;
    items: FeatureListItem[];
}

@Component({
    template: `
        <div *ngIf="block.title" class="list-title">{{ block.title }}</div>

        <div class="list">
            <a class="item" [url]="item.url" *ngFor="let item of block.items">
                <div class="copy">
                    <mat-icon *ngIf="item.icon">{{ item.icon }}</mat-icon>
                    <div class="title">{{ item.title }}</div>
                    <div class="summary" *ngIf="item.summary">{{ item.summary }}</div>
                    <div class="chips">
                        <div *ngIf="item.tag" class="tag">{{ item.tag }}</div>
                        <div *ngIf="item.date" class="date">{{ item.date | date: 'longDate' }}</div>
                    </div>
                </div>
                <div class="image" role="img" [responsiveBackgroundImage]="item.image"></div>
            </a>
        </div>
    `,
    styles: [`
        :host {
            display: flex;
            flex-direction: column;
            margin: 1em 0;
        }

        .list-title {
            padding: 4px 1em;
            background-color: #181a22;
            font-size: 90%;
        }

        .list {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 3em;
        }

        .title {
            font-size: 22pt;
        }

        .item {
            border: 1px solid #474b5b;
            display: flex;
            flex-direction: column;
            align-items: center;
            color: currentColor;
            background-color: #1d1f26;
            width: calc(50% - 1.5em);

            @media (width < 980px) {
                width: 100%;
            }

            .image {
                width: 100%;
                aspect-ratio: 1/1;
                background-size: cover;
                background-position: center;
                flex-shrink: 0;
            }

            .summary {
                font-family: 80%;
                color: #ccc;
                text-align: center;
            }

            .copy {
                margin: 1em 1em 1em 0;
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: 1em;
                padding: 1.5em;
                flex-grow: 1;

                .chips {
                    display: flex;
                    gap: 1em;
                    align-items: center;
                    color: #777;
                    flex-wrap: wrap;

                    .tag {
                        background: blue;
                        color: white;
                        padding: 0px 14px;
                    }
                }
            }
        }
    `]
})
export class ViewComponent extends ContentBlockViewComponent<FeatureListBlock> {
}

@Component({
    template: `
        <mat-form-field appearance="outline" floatLabel="always">
            <mat-label>List Title</mat-label>
            <input type="text" matInput [(ngModel)]="block.title" placeholder="Optional" />
        </mat-form-field>

        <div class="item-list" cdkDropList (cdkDropListDropped)="drop($event)">
            <ng-container *ngFor="let item of block.items">
                <div class="item" cdkDrag>
                    <div class="content-box-item-move-placeholder" *cdkDragPlaceholder></div>
                    <div cdkDragHandle>
                        <mat-icon>drag_handle</mat-icon>
                    </div>

                    <div class="copy">
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>Title</mat-label>
                            <input type="text" matInput [(ngModel)]="item.title" />
                        </mat-form-field>
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>Summary</mat-label>
                            <input type="text" matInput [(ngModel)]="item.summary" />
                        </mat-form-field>
                        <tyt-url-field
                            label="Link"
                            [(value)]="item.url"
                            ></tyt-url-field>
                        <mat-form-field appearance="outline" floatLabel="always">
                            <mat-label>Icon</mat-label>
                            <input type="text" matInput [(ngModel)]="item.icon" />
                        </mat-form-field>
                        <tyt-image-asset [(url)]="item.image"
                            [aspectRatio]="1/1"
                            [recommendedAspectRatio]="1/1"
                            recommendedAspectRatioLabel="1:1"
                            ></tyt-image-asset>
                    </div>

                    <div>
                        <button mat-icon-button (click)="remove(item)">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>
                </div>
            </ng-container>
        </div>
        <div class="actions">
            <button mat-raised-button color="primary" (click)="add()">
                <mat-icon>add</mat-icon>
                Add Item
            </button>
        </div>
    `,
    styles: [
        `
        mat-form-field {
            width: 100%;
        }

        ::ng-deep .content-box-item-move-placeholder {
            width: 100%;
            height: 10em;
            border: 4px dashed white;
        }

        .actions {
            display: flex;
            justify-content: flex-end;
        }

        .item-list {
            display: flex;
            flex-wrap: wrap;
            gap: 1em;
        }
        .item {
            display: flex;
            align-items: flex-start;
            width: calc(50% - 1em);

            @media (width < 810px) {
                width: 100%;
            }

            &.cdk-drag-preview {
                background-color: #171921;
                border: 4px dashed white;
            }

            .copy {
                display: flex;
                flex-direction: column;
                flex-grow: 1;
            }
        }
        `
    ]
})
export class EditorComponent extends ContentBlockEditorComponent<FeatureListBlock> {
    add() {
        this.block.items.push({
            title: '',
            summary: ''
        })
    }

    remove(item: FeatureListItem) {
        let index = this.block.items.indexOf(item);
        if (index >= 0) {
            this.block.items.splice(index, 1);
        }
    }

    drop(event: CdkDragDrop<string[]>) {
        moveItemInArray(this.block.items, event.previousIndex, event.currentIndex);
    }
}

export const FEATURE_LIST_BLOCK_TYPE: ContentBlockType<FeatureListBlock> = {
    id: 'feature-list',
    label: 'Feature List',
    icon: 'feed',
    template: {
        items: []
    },
    preview: {
        items: [
            {
                image: `/assets/operation-hope/support-c2a-bg.png`,
                title: 'Lorem ipsum dolor sit amet',
                summary: 'The quick brown fox jumps over the lazy dog',
                url: 'https://google.com'
            },
            {
                image: `/assets/flag-backdrop.png`,
                title: 'Fusce vitae pretium nibh',
                url: 'https://google.com'
            }
        ]
    },
    editorComponent: EditorComponent,
    viewComponent: ViewComponent
};
